import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';

const buildTrialState = (trialEndsAt, subscriptionStatus) => {
  const status = String(subscriptionStatus || '').toLowerCase();
  if (status === 'active') {
    return {
      expired: false,
      message: 'Subscription active.',
      timeLeftValue: null
    };
  }
  if (status && status !== 'trialing') {
    return {
      expired: true,
      message: 'Your AutoClever subscription is not active.',
      timeLeftValue: 0
    };
  }
  if (!trialEndsAt) {
    return { expired: false, message: '', timeLeftValue: null };
  }

  const futureDate = moment(trialEndsAt);
  const now = moment();
  const diff = futureDate.diff(now);
  const duration = moment.duration(diff);

  if (duration.asMilliseconds() <= 0) {
    return {
      expired: true,
      message:
        'Sorry your trial period has expired, if you would like to continue using AutoClever please subscribe below.',
      timeLeftValue: 0
    };
  }

  return {
    expired: false,
    message: `${Math.floor(duration.asDays())} days, ${duration.hours()} hours, ${duration.minutes()} minutes, ${duration.seconds()} seconds until trial ends.`,
    timeLeftValue: duration.asMilliseconds()
  };
};

const useSubscriptionStatus = () => {
  const auth = useAuth();
  const [trialState, setTrialState] = useState({
    expired: false,
    message: '',
    timeLeftValue: null
  });
  const { shop, stripeCustomerId } = useSelector((state) => state.settings);
  const accountShop = auth.user?.shop || null;
  const settingsMatchActiveShop = Boolean(
    shop?.id && accountShop?.id && Number(shop.id) === Number(accountShop.id)
  );
  const effectiveShop = settingsMatchActiveShop ? shop : accountShop || (shop?.id ? shop : null);
  const effectiveTrialEndsAt = effectiveShop?.trial_ends_at || effectiveShop?.trialEndsAt;
  const effectiveSubscriptionStatus = effectiveShop?.subscription_status || effectiveShop?.subscriptionStatus || '';

  useEffect(() => {
    const updateCountdown = () => {
      setTrialState(buildTrialState(effectiveTrialEndsAt, effectiveSubscriptionStatus));
    };

    updateCountdown();

    if (!effectiveTrialEndsAt || effectiveSubscriptionStatus !== 'trialing') {
      return undefined;
    }

    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [effectiveTrialEndsAt, effectiveSubscriptionStatus]);

  const subscriptionStatus = effectiveSubscriptionStatus;
  const accountAccess = accountShop?.operationalAccess;

  return {
    trialLeft: trialState.timeLeftValue,
    trialMessage: trialState.message,
    trialExpired: trialState.expired,
    subscriptionStatus,
    subscriptionActive: typeof accountAccess === 'boolean'
      ? accountAccess
      : subscriptionStatus === 'active' || (subscriptionStatus === 'trialing' && !trialState.expired),
    shop: effectiveShop,
    stripeCustomerId: settingsMatchActiveShop || !accountShop?.id ? stripeCustomerId : null,
    loading: !auth.isInitialized
  };
};

export default useSubscriptionStatus;
